import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getRentalMovements, 
  getActiveRentals, 
  returnRentalItem, 
  markOverdueRentals,
  getRentalStats 
} from '../../services/rentalService';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

const RentalManager = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [movements, setMovements] = useState([]);
  const [activeRentals, setActiveRentals] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('active'); // 'active', 'all', 'returned', 'overdue'
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [selectedRental, setSelectedRental] = useState(null);
  const [returnData, setReturnData] = useState({
    actualReturnDate: '',
    condition: 'good',
    notes: ''
  });

  useEffect(() => {
    const checkAccessAndLoadData = async () => {
      if (user?.companyId) {
        try {
          const { companyService } = await import('../../services/companyService');
          const access = await companyService.hasRentalAccess(user.companyId);
          setHasAccess(access);
          
          if (access) {
            await loadData();
          }
        } catch (error) {
          console.error('Erro ao verificar acesso:', error);
          setHasAccess(false);
        } finally {
          setLoading(false);
        }
      }
    };

    checkAccessAndLoadData();
  }, [user?.companyId]);

  const loadData = async () => {
    try {
      await markOverdueRentals(user.companyId);
      
      const [movementsData, activeData, statsData] = await Promise.all([
        getRentalMovements(user.companyId),
        getActiveRentals(user.companyId),
        getRentalStats(user.companyId)
      ]);
      
      setMovements(movementsData);
      setActiveRentals(activeData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const handleReturn = async () => {
    try {
      setLoading(true);
      await returnRentalItem(selectedRental.id, returnData);
      
      setShowReturnModal(false);
      setSelectedRental(null);
      setReturnData({
        actualReturnDate: '',
        condition: 'good',
        notes: ''
      });
      
      await loadData();
    } catch (error) {
      console.error('Erro ao registrar devolução:', error);
      alert('Erro ao registrar devolução. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const openReturnModal = (rental) => {
    setSelectedRental(rental);
    setReturnData({
      actualReturnDate: new Date().toISOString().split('T')[0],
      condition: 'good',
      notes: ''
    });
    setShowReturnModal(true);
  };

  const getFilteredData = () => {
    switch (filter) {
      case 'active':
        return activeRentals;
      case 'overdue':
        return activeRentals.filter(r => r.isOverdue);
      case 'returned':
        return movements.filter(m => m.movementType === 'rental_return');
      default:
        return movements;
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status, isOverdue) => {
    if (isOverdue) return 'bg-red-100 text-red-800';
    switch (status) {
      case 'rented': return 'bg-blue-100 text-blue-800';
      case 'returned': return 'bg-green-100 text-green-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status, isOverdue) => {
    if (isOverdue) return 'Atrasado';
    switch (status) {
      case 'rented': return 'Alugado';
      case 'returned': return 'Devolvido';
      case 'overdue': return 'Atrasado';
      default: return status;
    }
  };

  if (loading && movements.length === 0) {
    return <LoadingSpinner />;
  }

  // Verificação de acesso
  if (!hasAccess) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex flex-col items-center justify-center h-96 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 animate-fade-in">
          <div className="text-center max-w-md transform transition-all duration-300 hover:scale-105">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-orange-100">
                <svg className="h-10 w-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H8m13-9a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Acesso Restrito
            </h2>
            
            <p className="text-gray-600 mb-6 leading-relaxed">
              Sua empresa não tem acesso ao módulo de aluguéis. Entre em contato com o administrador para solicitar acesso a esta funcionalidade.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800 mb-1">
                    Para solicitar acesso
                  </h3>
                  <p className="text-sm text-blue-700">
                    Entre em contato com nossa equipe através do suporte técnico ou solicite ao administrador do sistema para habilitar o módulo de aluguéis para sua empresa.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 text-sm text-gray-500">
              <p>
                <strong>Funcionalidades do módulo:</strong>
              </p>
              <ul className="text-left space-y-1">
                <li>• Gestão completa de itens alugados</li>
                <li>• Controle de devoluções e prazos</li>
                <li>• Histórico de movimentações</li>
                <li>• Relatórios de aluguéis</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredData = getFilteredData();

  return (
    <div className="space-y-6">
      {/* Header com estatísticas */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestão de Aluguéis</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-sm text-blue-600 font-medium">Total de Aluguéis</div>
            <div className="text-2xl font-bold text-blue-900">{stats.totalRentals || 0}</div>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <div className="text-sm text-yellow-600 font-medium">Ativos</div>
            <div className="text-2xl font-bold text-yellow-900">{stats.activeRentals || 0}</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-sm text-red-600 font-medium">Atrasados</div>
            <div className="text-2xl font-bold text-red-900">{stats.overdueRentals || 0}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-sm text-green-600 font-medium">Devolvidos</div>
            <div className="text-2xl font-bold text-green-900">{stats.returnedRentals || 0}</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              filter === 'active' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Ativos ({stats.activeRentals || 0})
          </button>
          <button
            onClick={() => setFilter('overdue')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              filter === 'overdue' 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Atrasados ({stats.overdueRentals || 0})
          </button>
          <button
            onClick={() => setFilter('returned')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              filter === 'returned' 
                ? 'bg-green-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Devolvidos ({stats.returnedRentals || 0})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              filter === 'all' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Todos ({movements.length || 0})
          </button>
        </div>
      </div>

      {/* Tabela de movimentações */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item / Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de Saída
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Devolução Prevista
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Devolução Real
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Nenhuma movimentação encontrada
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.rewardName}</div>
                        <div className="text-sm text-gray-500">{item.customerName}</div>
                        <div className="text-xs text-gray-400">{item.customerDocument}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        getStatusColor(item.status, item.isOverdue)
                      }`}>
                        {getStatusLabel(item.status, item.isOverdue)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(item.actualDeliveryDate || item.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(item.expectedReturnDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {formatDate(item.actualReturnDate)}
                    </td>
                    <td className="px-6 py-4">
                      {item.movementType === 'rental_out' && !item.actualReturnDate && (
                        <button
                          onClick={() => openReturnModal(item)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Registrar Devolução
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Devolução */}
      <Modal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        title="Registrar Devolução"
      >
        <form onSubmit={(e) => { e.preventDefault(); handleReturn(); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data de Devolução *
            </label>
            <input
              type="date"
              value={returnData.actualReturnDate}
              onChange={(e) => setReturnData(prev => ({ ...prev, actualReturnDate: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Condição do Item
            </label>
            <select
              value={returnData.condition}
              onChange={(e) => setReturnData(prev => ({ ...prev, condition: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="good">Boa condição</option>
              <option value="damaged">Danificado</option>
              <option value="lost">Perdido</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observações
            </label>
            <textarea
              value={returnData.notes}
              onChange={(e) => setReturnData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
              placeholder="Observações sobre a devolução..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowReturnModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Registrar Devolução'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default RentalManager;
